import { useTranslation } from 'react-i18next'
import { Box, Typography, CircularProgress, Chip } from '@mui/material'
import { useParams } from 'react-router-dom'
import { MapContainer, Marker, Polyline, Popup } from 'react-leaflet'
import MapTileLayer from '../../components/MapTileLayer'
import 'leaflet/dist/leaflet.css'
import L from 'leaflet'
import { usePublicTrack } from '../../hooks/useShareLinks'

// Simple marker icon — color set dynamically per brand_color
function makeVehicleIcon(color: string) {
  return L.divIcon({
    className: '',
    html: `<div style="width:32px;height:32px;border-radius:50%;background:${color};border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/></svg>
    </div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  })
}

const destIcon = L.divIcon({
  className: '',
  html: `<div style="width:28px;height:28px;border-radius:50%;background:#ef4444;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center">
    <svg width="14" height="14" viewBox="0 0 24 24" fill="white"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
  </div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 28],
})

export default function ShareTrackingPage() {
  const { t } = useTranslation()
  const { token } = useParams<{ token: string }>()
  const { data, isLoading, isError } = usePublicTrack(token ?? '')

  if (isLoading) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f8fafc' }}>
        <CircularProgress sx={{ color: '#34A8E0' }} />
      </Box>
    )
  }

  if (isError || !data) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f8fafc', flexDirection: 'column', gap: 2 }}>
        <Typography variant="h5" color="#1e293b" fontWeight={700}>{t('share.title')}</Typography>
        <Typography color="#64748b">{t('share.invalidLink')}</Typography>
      </Box>
    )
  }

  const themeColor = data.brand_color || '#34A8E0'
  const vehicleIcon = makeVehicleIcon(themeColor)

  const center: [number, number] = data.latitude && data.longitude
    ? [data.latitude, data.longitude]
    : [33.3152, 44.3661]

  const hasDest = data.dest_latitude != null && data.dest_longitude != null
  const hasDevice = data.latitude != null && data.longitude != null

  return (
    <Box sx={{ height: '100vh', position: 'relative' }}>
      <MapContainer center={center} zoom={15} style={{ height: '100%', width: '100%' }}>
        <MapTileLayer />
        {hasDevice && (
          <Marker position={[data.latitude!, data.longitude!]} icon={vehicleIcon}>
            <Popup>
              <strong>{data.device_name}</strong>
              {data.vehicle_type && <><br />{data.vehicle_type}</>}
              {data.speed != null && <><br />{t('share.speed')}: {data.speed.toFixed(0)} km/h</>}
              {data.last_seen_at && <><br /><small>{new Date(data.last_seen_at).toLocaleString()}</small></>}
            </Popup>
          </Marker>
        )}
        {hasDest && (
          <Marker position={[data.dest_latitude!, data.dest_longitude!]} icon={destIcon}>
            <Popup>
              <strong>{data.dest_label || 'Destination'}</strong>
            </Popup>
          </Marker>
        )}
        {hasDevice && hasDest && (
          <Polyline
            positions={[
              [data.latitude!, data.longitude!],
              [data.dest_latitude!, data.dest_longitude!],
            ]}
            pathOptions={{ color: themeColor, dashArray: '10, 10', weight: 3 }}
          />
        )}
      </MapContainer>

      {/* Overlay info bar */}
      <Box sx={{
        position: 'absolute', top: 16, left: '50%', transform: 'translateX(-50%)',
        zIndex: 1000, bgcolor: 'rgba(255,255,255,0.95)', borderRadius: 2, px: 3, py: 1.5,
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)', display: 'flex', alignItems: 'center', gap: 2,
      }}>
        {data.logo_url ? (
          <Box component="img" src={data.logo_url} alt="Logo" sx={{ height: 28, maxWidth: 120, objectFit: 'contain' }} />
        ) : (
          <Typography variant="subtitle1" fontWeight={700} color="#1e293b">
            {data.device_name}
          </Typography>
        )}
        {data.speed != null && (
          <Chip label={`${data.speed.toFixed(0)} km/h`} size="small" sx={{ borderColor: themeColor, color: themeColor }} variant="outlined" />
        )}
        {data.eta_minutes != null && (
          <Chip label={`ETA: ${data.eta_minutes} min`} size="small" sx={{ bgcolor: themeColor, color: '#fff', fontWeight: 600 }} />
        )}
        {data.last_seen_at && (
          <Typography variant="caption" color="#94a3b8">
            {t('share.lastSeen')}: {new Date(data.last_seen_at).toLocaleTimeString()}
          </Typography>
        )}
        <Chip
          label={`${t('share.expires')}: ${new Date(data.expires_at).toLocaleString()}`}
          size="small" variant="outlined"
          sx={{ fontSize: 11 }}
        />
      </Box>

      {/* Branding */}
      <Box sx={{
        position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)',
        zIndex: 1000, bgcolor: 'rgba(255,255,255,0.9)', borderRadius: 1, px: 2, py: 0.5,
        display: 'flex', alignItems: 'center', gap: 1,
      }}>
        {data.logo_url ? (
          <>
            <Typography variant="caption" color="#94a3b8">Powered by</Typography>
            <Box component="img" src={data.logo_url} alt="Logo" sx={{ height: 16, objectFit: 'contain' }} />
          </>
        ) : (
          <Typography variant="caption" color="#94a3b8">
            Powered by OmniTrace
          </Typography>
        )}
      </Box>
    </Box>
  )
}
